'use client';
import { useState } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { projects } from '@/data/projects';
import OrbitalObject from '../ui/OrbitalObject';
import WebsitePreview from '../ui/WebsitePreview';
import TechStack from '../ui/TechStack';
import Link from 'next/link';

type SortKey = 'title' | 'type';
type SortOrder = 'asc' | 'desc';

export default function Projects({ initialType = 'all', initialSubType = 'all' }: { initialType?: string, initialSubType?: string } = {}) {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>(initialType);
  const [selectedSubType, setSelectedSubType] = useState<string>(initialSubType);
  const [sortKey, setSortKey] = useState<SortKey>('title');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());

  const toggleDescription = (projectId: string) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  const types = ['all', ...new Set(projects.map(project => project.type))];
  const subTypes = ['all', ...new Set(projects.filter(p => p.type === selectedType && p.subType).map(p => p.subType))];

  const filteredProjects = projects
    .filter(project => {
      // Filter by type
      const matchesType = selectedType === 'all' || project.type === selectedType;
      
      // Filter by subtype
      const matchesSubType = selectedType !== 'all' ? (selectedSubType === 'all' || (project.subType && project.subType.toLowerCase() === selectedSubType.toLowerCase())) : true;
      
      // Filter by search query
      const matchesSearch = searchQuery === '' || 
        project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.technologies.some(tech => tech.toLowerCase().includes(searchQuery.toLowerCase()));
      
      return matchesType && matchesSubType && matchesSearch;
    })
    .sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];
      return sortOrder === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  // Create 20 orbital objects with varying properties
  const orbitalConfigs = Array.from({ length: 20 }, (_, index) => ({
    color: [
      "#4f83ff", "#83ff4f", "#ff4f83", "#ff834f", "#834fff",
      "#4fff83", "#ff4fff", "#ffff4f", "#4fffff", "#ffffff",
      "#ff6b6b", "#4ecdc4", "#45b7d1", "#96ceb4", "#feca57",
      "#ff9ff3", "#54a0ff", "#5f27cd", "#00d2d3", "#ff9f43"
    ][index % 20],
    size: 4 + (index % 8) + 2, // Sizes from 6 to 13
    orbitDuration: 15 + (index % 25) + 5, // Duration from 20 to 44 seconds
    orbitRadius: 80 + (index % 15) * 20, // Radius from 80 to 360
    delay: (index % 10) * 2, // Delays from 0 to 18 seconds
  }));

  return (
    <section id="projects" className="py-20 bg-black relative overflow-hidden">
      {/* Map through 20 orbital objects */}
      {orbitalConfigs.map((config, index) => (
        <div key={index} className="absolute inset-0" style={{ left: '50%', top: '50%' }}>
          <OrbitalObject
            color={config.color}
            size={config.size}
            orbitDuration={config.orbitDuration}
            orbitRadius={config.orbitRadius}
            delay={config.delay}
          />
        </div>
      ))}

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <h2 className="text-3xl font-bold mb-8 text-white">Projects ({filteredProjects.length})</h2>

        <div className="flex flex-wrap gap-4 mb-8">
          {/* Search Bar */}
          <div className="flex items-center space-x-4 flex-1 min-w-[250px]">
            <label htmlFor="project-search" className="font-medium text-gray-300">Search:</label>
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                id="project-search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search projects..."
                className="w-full bg-zinc-800 border-zinc-700 text-gray-300 border rounded-md px-3 py-1.5 pl-9 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
              <svg 
                className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                  aria-label="Clear search"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <label htmlFor="project-filter" className="font-medium text-gray-300">Filter:</label>
            <select
              id="project-filter"
              value={selectedType}
              onChange={(e) => {
                setSelectedType(e.target.value);
                setSelectedSubType('all');
              }}
              className="bg-zinc-800 border-zinc-700 text-gray-300 border rounded-md px-3 py-1 focus:ring-2 focus:ring-blue-500"
            >
              {types.map(type => (
                <option key={type} value={type} className="bg-zinc-800">
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {selectedType !== 'all' && subTypes.length > 1 && (
            <div className="flex items-center space-x-4">
              <label htmlFor="project-sub-filter" className="font-medium text-gray-300">Category:</label>
              <select
                id="project-sub-filter"
                value={selectedSubType}
                onChange={(e) => setSelectedSubType(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-gray-300 border rounded-md px-3 py-1 focus:ring-2 focus:ring-blue-500"
              >
                {subTypes.map((subType: any) => (
                  <option key={subType} value={subType} className="bg-zinc-800">
                    {subType === 'all' ? 'All' : typeof subType === 'string' ? subType.toUpperCase() : subType}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex items-center space-x-4">
            <label htmlFor="project-sort" className="font-medium text-gray-300">Sort by:</label>
            <select
              id="project-sort"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              className="bg-zinc-800 border-zinc-700 text-gray-300 border rounded-md px-3 py-1 focus:ring-2 focus:ring-blue-500"
            >
              <option value="title" className="bg-zinc-800">Title</option>
              <option value="type" className="bg-zinc-800">Type</option>
            </select>
            <button
              onClick={() => setSortOrder(order => order === 'asc' ? 'desc' : 'asc')}
              className="p-1 hover:bg-zinc-800 text-gray-300 rounded transition-colors"
              aria-label={`Sort ${sortOrder === 'asc' ? 'descending' : 'ascending'}`}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>

        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {filteredProjects.map((project, index) => (
            <motion.div
              key={project.id}
              variants={item}
              className="bg-zinc-800/80 backdrop-blur-sm border-zinc-700 border rounded-lg overflow-hidden hover:shadow-[0_0_25px_rgba(79,131,255,0.15)] transition-all duration-300 group flex flex-col"
              whileHover={{ scale: 1.02 }}
            >
              {/* Use WebsitePreview for all projects */}
              {project.websitePreviewImage ? (
                <div className="relative w-full h-48">
                  <WebsitePreview
                    src={project.websitePreviewImage}
                    alt={project.title}
                    title={project.title}
                    className="w-full h-full rounded-t-lg"
                    priority={index < 4}
                  />
                </div>
              ) : project.imageUrl ? (
                <div className="relative w-full h-48 overflow-hidden">
                  <Image
                    src={project.imageUrl}
                    alt={project.title}
                    fill
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 25vw, 284px"
                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/50 to-transparent" />
                </div>
              ) : null}

              <div className="p-4 sm:p-6 flex flex-col flex-grow">
                {/* Title */}
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-white group-hover:text-blue-300 transition-colors truncate">
                  {project.title}
                </h3>

                {/* Description - Expandable */}
                <div className="mb-4 flex-grow">
                  <p
                    className={`text-gray-300 text-sm sm:text-base leading-relaxed ${!expandedDescriptions.has(project.id)
                      ? 'line-clamp-3'
                      : ''
                      }`}
                    style={
                      !expandedDescriptions.has(project.id)
                        ? {
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }
                        : {}
                    }
                  >
                    {project.description}
                  </p>

                  {/* Show more/less button - only if description is long enough */}
                  {project.description.length > 150 && (
                    <button
                      onClick={() => toggleDescription(project.id)}
                      className="text-blue-400 hover:text-blue-300 text-sm mt-2 transition-colors"
                    >
                      {expandedDescriptions.has(project.id) ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </div>

                {/* Technologies Section - Show only 3 tags */}
                <div className="mb-4">
                  <TechStack technologies={project.technologies} maxVisible={4} />
                </div>

                {/* Separator Line */}
                <div className="border-t border-zinc-700 mb-4"></div>

                {/* Action Buttons - Same line layout */}
                <div className="flex gap-2">
                  {/* View Button - Different for mobile apps vs web projects */}
                  {project.type === 'mobile' ? (
                    <Link
                      href={`/mobile-apps/${project.id}`}
                      className="flex items-center justify-center space-x-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-all duration-200 text-xs font-medium flex-1"
                      title="View Mobile App Details"
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                      <span className="truncate">View App</span>
                    </Link>
                  ) : project.type === 'web' ? (
                    <>
                      {/* Live Button for Web Projects */}
                      {project.deployedUrl && (
                        <a
                          href={project.deployedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center space-x-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-all duration-200 text-xs font-medium flex-1"
                          title="View Live Website"
                        >
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      )}
                      {/* Details Button for Web Projects */}
                      <Link
                        href={`/websites/${project.id}`}
                        className="flex items-center justify-center space-x-1 px-3 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-md transition-all duration-200 text-xs font-medium flex-1"
                        title="View Website Details"
                      >
                        <svg className="w-4 h-4 flex-shrink-0" aria-hidden="true" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="truncate">Details</span>
                      </Link>
                    </>
                  ) : project.deployedUrl ? (
                    <a
                      href={project.deployedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center space-x-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-all duration-200 text-xs font-medium flex-1"
                      title="View Live Project"
                    >
                      <svg className="w-4 h-4 flex-shrink-0" aria-hidden="true" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      <span className="truncate">Live</span>
                    </a>
                  ) : null}

                  {/* GitHub Button */}
                  {project.githubUrl && !project.isPrivate ? (
                    <a
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center space-x-1 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all duration-200 text-xs font-medium flex-1"
                      title="View Source Code"
                    >
                      <svg className="w-4 h-4 flex-shrink-0" aria-hidden="true" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0C5.374 0 0 5.373 0 12 0 17.302 3.438 21.8 8.207 23.387c.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.30 3.297-1.30.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" />
                      </svg>
                      <span className="truncate">Code</span>
                    </a>
                  ) : project.githubUrl && project.isPrivate ? (
                    <div
                      className="flex items-center justify-center space-x-1 px-3 py-2 bg-gray-800 text-gray-300 rounded-md cursor-not-allowed text-xs font-medium flex-1"
                      title="Private Repository"
                    >
                      <svg className="w-4 h-4 flex-shrink-0" aria-hidden="true" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                      </svg>
                      <span className="truncate">Private</span>
                    </div>
                  ) : null}
                </div>

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg pointer-events-none" />
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}